import { createApi, fetchBaseQuery, type FetchArgs, type BaseQueryFn } from "@reduxjs/toolkit/query/react";
import type SessionClient from "util/SessionClient";
import {
  handleEmailNotVerified,
  handleUnauthorized,
  isForbiddenStatus,
  isUnauthorizedStatus,
  type SessionRedirectOptionsT,
} from "util/sessionExpiry";

const baseUrl = `${process.env.REACT_APP_POWUR_API_URL}/json_web`;
const rawBaseQuery = fetchBaseQuery({ baseUrl });

type SessionFactoryT = () => SessionClient;

let createSession: SessionFactoryT | null = null;

// SessionClient imports the store, whose reducers import endpoints built on this api, so this module
// can't import it without a cycle. The app registers the factory at startup instead.
export const registerSessionFactory = (factory: SessionFactoryT) => {
  createSession = factory;
};

const setHeaders = (session: SessionClient | null, body?: BodyInit) => {
  const headers = new Headers();
  if (!(body instanceof FormData)) {
    headers.set("Content-Type", "application/json; charset=utf-8");
  }
  headers.set("Accept", "*/*");
  if (session?.jwt) {
    headers.set("Authorization", `Bearer ${session.jwt}`);
  }
  return headers;
};

const customBaseQuery: BaseQueryFn<string | FetchArgs, unknown, unknown, SessionRedirectOptionsT> = async (
  args,
  api,
  extraOptions,
) => {
  const session = createSession?.() ?? null;

  const fetchArgs: FetchArgs = typeof args === "string" ? { url: args } : args;

  const uri = new URL(fetchArgs.url, baseUrl);
  const sessionUserId = session?.user?.props?.id;
  if (session?.jwt && sessionUserId) {
    uri.searchParams.append("t_id", sessionUserId);
  }

  const finalArgs: FetchArgs = {
    ...fetchArgs,
    url: uri.pathname + uri.search,
    headers: setHeaders(session, fetchArgs.body),
  };

  const result = await rawBaseQuery(finalArgs, api, extraOptions);

  if (result.error && typeof result.error === "object" && "status" in result.error) {
    const { status } = result.error;

    if (isForbiddenStatus(status) && !extraOptions?.noEmailVerifiedRedirect) {
      const errData = (result.error as { data?: { error?: { message?: string } } }).data;
      if (errData?.error?.message === "email_not_verified") {
        handleEmailNotVerified();
      }
    }

    if (isUnauthorizedStatus(status)) {
      handleUnauthorized(session, extraOptions);
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: customBaseQuery,
  endpoints: () => ({}),
});
